import React, { useEffect, useRef, useMemo, useCallback } from 'react';

import { graphql, useFragment } from 'react-relay';
import type { JiraProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { ProjectExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-projects-common/src/ui/project-expandable-menu-item/ProjectExpandableMenuItem.tsx';
import { useOpenable } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-openable/index.tsx';
import type { NavigationProject } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-jpd-nav-views/src/common/types.tsx';
import type { NavigationView } from '@jira/polaris__component-navigation-store/src/NavigationView.types.tsx';
import { usePathParam, useRouterActions } from '@atlassian/react-resource-router';
import { AnyAri } from '@atlassian/ari/any-ari';
import { getViewUrl } from '../common/utils/router/get-view-url.tsx';
import type { JpdProjectMenuLoader_project$key } from './__generated__/JpdProjectMenuLoader_project.graphql';
import { AsyncActionButtons as ActionButtons } from './action-buttons/AsyncActionButtons.tsx';

import { AsyncJpdProjectMenuContent as JpdProjectMenuContent } from './AsyncJpdProjectMenuContent.tsx';

export type JpdProjectMenuLoaderProps = {
	projectFragment: JpdProjectMenuLoader_project$key;
	menuItemButtonRef?: React.RefObject<HTMLButtonElement>;
};

export const JpdProjectMenuLoader = ({
	projectFragment,
	menuItemButtonRef,
}: JpdProjectMenuLoaderProps): React.JSX.Element => {
	const { push } = useRouterActions();

	const [projectKeyFromUrl] = usePathParam('projectKey');
	const projectKeyRef = useRef(projectKeyFromUrl);

	useEffect(() => {
		projectKeyRef.current = projectKeyFromUrl;
	}, [projectKeyFromUrl]);

	const project = useFragment<JpdProjectMenuLoader_project$key>(
		graphql`
			fragment JpdProjectMenuLoader_project on JiraProject {
				projectKey: key @required(action: THROW)
				projectType @required(action: THROW)
				id @required(action: THROW)
				isFavourite
				...ProjectExpandableMenuItem
			}
		`,
		projectFragment,
	);

	const navigationProject = useMemo(() => {
		const navigationProjectData: NavigationProject = {
			projectId: project.id,
			projectKey: project.projectKey,
			isFavourite: project.isFavourite ?? false,
			// oxlint-disable-next-line typescript/consistent-type-assertions
			projectType: project.projectType as JiraProjectType,
		};
		try {
			return {
				...navigationProjectData,
				projectId: AnyAri.parse(project.id).resourceId,
			};
		} catch {
			return navigationProjectData;
		}
	}, [project.id, project.isFavourite, project.projectKey, project.projectType]);

	const isProjectSelected = project.projectKey ? projectKeyFromUrl === project.projectKey : false;
	// oxlint-disable-next-line react/react-compiler
	const isProjectChanged = projectKeyRef.current !== projectKeyFromUrl;

	// Track project expansion state, selected project is expanded by default
	const { isOpen: isProjectExpanded, onToggle } = useOpenable(isProjectSelected);

	const onViewCreated = useCallback(
		(view: NavigationView) => {
			if (isProjectSelected) {
				return;
			}

			push(getViewUrl({ container: navigationProject, view }));
		},
		[isProjectSelected, push, navigationProject],
	);

	return (
		<ProjectExpandableMenuItem
			actionsOnHover={
				<ActionButtons
					projectKey={project.projectKey}
					onViewCreated={onViewCreated}
					isProjectExpanded={isProjectExpanded}
					navigationProject={navigationProject}
				/>
			}
			projectData={project}
			menuItemButtonRef={menuItemButtonRef}
			onToggle={onToggle}
		>
			<JpdProjectMenuContent
				navigationProject={navigationProject}
				onViewCreated={onViewCreated}
				isProjectSelected={isProjectSelected}
				isProjectChanged={isProjectChanged}
			/>
		</ProjectExpandableMenuItem>
	);
};
